import { useEffect, useState } from "react";
import { api, LOGO_URL, resolveImage } from "@/lib/api";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Plus, ImagePlus, X, Info } from "lucide-react";
import SnapshotsPanel from "@/components/SnapshotsPanel";

function Row({ p, onEdit, onDelete, onRename }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: p.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  const low = p.inventory <= 10;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-4 p-4 rounded-lg border ${
        isDragging ? "border-[#FF3D8B]" : "border-white/10"
      } bg-[#0f0f0f]`}
      data-testid={`product-row-${p.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-white/40 hover:text-[#FF3D8B] cursor-grab active:cursor-grabbing"
        data-testid={`product-drag-${p.id}`}
        aria-label="Drag to reorder"
      >
        <GripVertical size={18} />
      </button>
      <img
        src={resolveImage(p.image_url, 128)}
        alt={p.name}
        className="w-14 h-14 rounded-md object-cover bg-white/5 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold truncate">{p.name}</div>
        <div className="text-white/50 text-xs truncate flex items-center gap-1">
          <span>/product/{p.id}</span>
          <button
            onClick={() => onRename(p)}
            data-testid={`product-rename-${p.id}`}
            className="text-[#A855F7] hover:text-[#c084fc] uppercase tracking-widest text-[10px] ml-1"
            title="Rename URL slug"
          >
            rename
          </button>
        </div>
        {p.tagline && <div className="text-white/40 text-[11px] truncate mt-0.5">{p.tagline}</div>}
      </div>
      <div className="hidden md:block text-right">
        <div className="text-white font-semibold">${p.price.toFixed(2)}</div>
        <div className={`text-xs ${low ? "text-red-400" : "text-white/40"}`}>
          {p.inventory} in stock
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onEdit(p)}
          data-testid={`product-edit-${p.id}`}
          className="px-3 py-1.5 rounded-full border border-white/15 text-white/80 text-xs uppercase tracking-widest hover:border-[#A855F7] hover:text-[#A855F7] transition-colors"
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(p)}
          data-testid={`product-delete-${p.id}`}
          className="text-white/40 hover:text-red-400 p-2"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </li>
  );
}

function ProductModal({ initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "",
    tagline: initial?.tagline || "",
    description: initial?.description || "",
    price: initial?.price ?? 12.99,
    inventory: initial?.inventory ?? 100,
    image_url: initial?.image_url || LOGO_URL,
    notes: (initial?.notes || []).join(", "),
    badge: initial?.badge || "",
    active: initial?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    const payload = {
      name: form.name,
      tagline: form.tagline,
      description: form.description,
      price: parseFloat(form.price),
      inventory: parseInt(form.inventory),
      image_url: form.image_url || LOGO_URL,
      notes: form.notes.split(",").map((s) => s.trim()).filter(Boolean),
      badge: form.badge || null,
      active: form.active,
    };
    try {
      if (isNew) {
        await api.post("/admin/products", payload, { withCredentials: true });
      } else {
        await api.patch(`/admin/products/${initial.id}`, payload, { withCredentials: true });
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 overflow-y-auto py-10"
      onClick={onClose}
      data-testid="product-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl rounded-2xl border border-[#FF3D8B]/30 bg-[#0a0a0a] p-8"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-3xl text-white">
            {isNew ? "New Product" : `Edit ${initial.name}`}
          </h3>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white" data-testid="product-modal-close">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2 flex items-center gap-4">
            <img
              src={resolveImage(form.image_url)}
              alt=""
              className="w-24 h-24 rounded-md object-cover bg-white/5 border border-white/10"
            />
            <div>
              <label className="inline-flex items-center gap-2 cursor-pointer px-4 py-2 rounded-full border border-white/15 text-white/80 text-xs uppercase tracking-widest hover:border-[#FF3D8B] hover:text-[#FF3D8B] transition-colors">
                <ImagePlus size={14} />
                {uploading ? "Uploading…" : "Upload Image"}
                <input
                  type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                  data-testid="product-image-upload"
                />
              </label>
              <div className="text-white/40 text-xs mt-2">PNG, JPG, or WEBP up to 8MB</div>
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className="block text-white/60 text-xs uppercase tracking-widest mb-1">Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="product-name-input"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3D8B]" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-white/60 text-xs uppercase tracking-widest mb-1">Tagline</label>
            <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3D8B]" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-white/60 text-xs uppercase tracking-widest mb-1">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3D8B]" />
          </div>
          <div>
            <label className="block text-white/60 text-xs uppercase tracking-widest mb-1">Price ($)</label>
            <input type="number" step="0.01" min="0" required value={form.price}
              data-testid="product-price-input"
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3D8B]" />
          </div>
          <div>
            <label className="block text-white/60 text-xs uppercase tracking-widest mb-1">Inventory</label>
            <input type="number" min="0" required value={form.inventory}
              data-testid="product-inventory-input"
              onChange={(e) => setForm({ ...form, inventory: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3D8B]" />
          </div>
          <div>
            <label className="block text-white/60 text-xs uppercase tracking-widest mb-1">Badge (optional)</label>
            <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })}
              placeholder="Bestseller / New / Hot / …"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3D8B]" />
          </div>
          <div>
            <label className="block text-white/60 text-xs uppercase tracking-widest mb-1">Notes (comma-sep)</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Peach, Vanilla, Musk"
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-white focus:outline-none focus:border-[#FF3D8B]" />
          </div>
          <div className="sm:col-span-2">
            <label className="inline-flex items-center gap-2 text-white/80 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="accent-[#FF3D8B]" />
              Active (visible in shop)
            </label>
          </div>
        </div>

        {error && <div className="mt-4 text-sm text-red-400" data-testid="product-modal-error">{error}</div>}

        <div className="mt-8 flex justify-end gap-3">
          <button type="button" onClick={onClose}
            className="px-5 py-2.5 rounded-full border border-white/15 text-white/80 uppercase tracking-widest text-xs">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            data-testid="product-modal-save"
            className="px-6 py-2.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] disabled:opacity-40 transition-colors">
            {busy ? "Saving…" : isNew ? "Create" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function ProductManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/products", { withCredentials: true });
    setProducts(data.products);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Reload when SnapshotsPanel restores a snapshot
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("bb:products-reload", handler);
    return () => window.removeEventListener("bb:products-reload", handler);
  }, []);

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = products.findIndex((p) => p.id === active.id);
    const newIdx = products.findIndex((p) => p.id === over.id);
    const next = arrayMove(products, oldIdx, newIdx);
    setProducts(next); // optimistic
    try {
      await api.post("/admin/products/reorder", { order: next.map((p) => p.id) }, { withCredentials: true });
    } catch {
      load();
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`, { withCredentials: true });
    load();
  };

  const slugSuggestion = (name) =>
    (name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const onRename = async (p) => {
    const suggested = slugSuggestion(p.name);
    const input = window.prompt(
      `Rename URL slug for "${p.name}"\n\n` +
      `Current: /product/${p.id}\n\n` +
      `New slug (lowercase letters, digits, hyphens):`,
      suggested || p.id
    );
    if (!input) return;
    const newId = slugSuggestion(input);
    if (!newId || newId === p.id) return;
    try {
      const { data } = await api.post(
        `/admin/products/${p.id}/rename`,
        { new_id: newId },
        { withCredentials: true }
      );
      if (data?.renamed) {
        alert(`Renamed to /product/${data.id}. Old URL /product/${data.old_id} now redirects.`);
      }
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Rename failed.");
    }
  };

  return (
    <section data-testid="product-manager">
      {/* Persistence guidance — matters because prod DBs reset on redeploy */}
      <div
        data-testid="product-persistence-banner"
        className="mb-5 rounded-xl border border-[#A855F7]/30 bg-[#A855F7]/[0.06] px-4 py-3 flex items-start gap-3 text-xs text-white/80 leading-relaxed"
      >
        <Info size={14} className="mt-0.5 text-[#A855F7] shrink-0" />
        <div>
          <div className="font-semibold text-white mb-0.5">
            Edits here are auto-saved to <code className="text-[#A855F7]">products.json</code> in the codebase.
          </div>
          <div className="text-white/60">
            Make product changes on the <span className="text-white">preview</span> site, then click{" "}
            <span className="text-white">Deploy → Redeploy</span> — your edits ship with the code and survive prod DB refreshes.
            Edits on the live site itself will be wiped on the next deploy.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl text-white">Products</h2>
        <button
          onClick={() => setShowNew(true)}
          data-testid="add-product-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#ff5ba0] transition-colors"
        >
          <Plus size={14} /> New Product
        </button>
      </div>

      <SnapshotsPanel />

      {loading ? (
        <div className="text-white/50 text-sm">Loading products…</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={products.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-2">
              {products.map((p) => (
                <Row key={p.id} p={p} onEdit={setEditing} onDelete={onDelete} onRename={onRename} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {(editing || showNew) && (
        <ProductModal
          initial={editing}
          onClose={() => { setEditing(null); setShowNew(false); }}
          onSaved={() => { setEditing(null); setShowNew(false); load(); }}
        />
      )}
    </section>
  );
}
